import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import { ChipSelect, Field, Page, PageHead, Reveal, Select } from '../components/UI';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';
import { HEIGHTS } from '../utils/format';

const AGES = Array.from({ length: 40 }, (_, i) => 18 + i);

export default function Preferences() {
  const { user, setUser, meta, toast } = useApp();
  const o = meta?.options || {};
  const navigate = useNavigate();
  const [p, setP] = useState(() => ({ religions: [], motherTongues: [], maritalStatus: [], cities: [], qualifications: [], professions: [], diets: [], ...user.partnerPrefs }));
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setP((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...p, minAge: Number(p.minAge) || undefined, maxAge: Number(p.maxAge) || undefined, minHeightCm: Number(p.minHeightCm) || undefined, maxHeightCm: Number(p.maxHeightCm) || undefined };
      const r = await api('/user/me', { method: 'PUT', body: { partnerPrefs: body } });
      setUser(r.user);
      toast('Preferences saved — your matches are refreshing ✨', 'success');
      navigate('/matches');
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const groups = [
    ['Religion', 'religions', o.RELIGIONS],
    ['Mother tongue', 'motherTongues', o.MOTHER_TONGUES],
    ['Marital status', 'maritalStatus', o.MARITAL],
    ['Preferred cities', 'cities', o.CITIES],
    ['Qualification', 'qualifications', o.QUALIFICATIONS],
    ['Profession', 'professions', o.PROFESSIONS],
    ['Diet', 'diets', o.DIETS],
  ];

  return (
    <Page>
      <div className="container narrow">
        <PageHead eyebrow="Partner preferences" title="Who would you like to meet?" sub="We use these to pick your daily matches. Leave “Any” to stay open." />
        <Reveal className="card form-card">
          <h3 className="sub-h"><Icon name="calendar" size={17} /> Age & height</h3>
          <div className="row-2">
            <Field label="Age">
              <div className="range-row">
                <Select value={p.minAge} onChange={set('minAge')} options={AGES.map(String)} placeholder="Min" />
                <span>to</span>
                <Select value={p.maxAge} onChange={set('maxAge')} options={AGES.map(String)} placeholder="Max" />
              </div>
            </Field>
            <Field label="Height">
              <div className="range-row">
                <Select value={p.minHeightCm} onChange={set('minHeightCm')} options={HEIGHTS.map((h) => ({ value: h.cm, label: h.label }))} placeholder="Any" />
                <span>to</span>
                <Select value={p.maxHeightCm} onChange={set('maxHeightCm')} options={HEIGHTS.map((h) => ({ value: h.cm, label: h.label }))} placeholder="Any" />
              </div>
            </Field>
          </div>
          {groups.map(([label, k, opts], i) => (
            <motion.div key={k} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}>
              <Field label={label}>
                <ChipSelect options={opts || []} value={p[k] || []} onChange={set(k)} />
              </Field>
            </motion.div>
          ))}
          <Field label="In my own words" hint="Shown on your profile under “What I'm looking for”">
            <textarea className="input" rows={3} maxLength={500} value={p.about || ''} onChange={set('about')} placeholder="Someone kind, ambitious and family-oriented…" />
          </Field>
          <div className="form-card__foot">
            <motion.button whileTap={{ scale: 0.96 }} className="btn btn--primary btn--lg" disabled={busy} onClick={save}>
              {busy ? 'Saving…' : 'Save & see matches'} <Icon name="arrowRight" size={18} />
            </motion.button>
          </div>
        </Reveal>
      </div>
    </Page>
  );
}
